import React, {useCallback, useMemo, useState} from "react";
import {NavLink, useNavigate} from "react-router-dom";
import PropTypes from "prop-types";

import {Login} from "../../components/Login/Login";
import {ACDelcoLogo, CountryOverrideSelect, LanguageSelect} from "../../components/UI";
import {
  Divider,
  LoadingIcon,
  Menu,
  MenuItem,
  Navbar,
  NavItem,
  NavItemGroup,
  useRegionContext,
  useTranslations
} from "tds_shared_ui";

import {ShoppingCartIcon} from "../../components/Cart/ShoppingCartIcon";
import {NotificationIcon} from "../../components/Notifications/NotificationIcon";
import {RpoLookupService, SpsCallibration,} from "../../services";
import "./AppHeader.css";
import {ResourceLoginCheck} from "../../components/UI/ResourceLoginCheck";
import {useUserContext} from "../../context/UserContext";
import {useLinksContext} from "../../context/LinksContext";

const navItems = [
  {
    label: "LABEL_SUBSCRIPTIONS",
    link: "/subscriptions",
    display: function (regionConfig) {
      return regionConfig.features.showSubscriptionNavItem;
    },
  },
  {
    label: "LABEL_SERVICE_INFORMATION",
    link: "/subscriptions",
    display: function (regionConfig) {
      return regionConfig.features.showServiceInformationNavItem;
    },
  },
  {
    label: "LABEL_HOW_TO_BUY",
    link: "/howtobuy",
    display: function (regionConfig) {
      return regionConfig.features.showHowToBuyNavItem;
    },
  },
  {
    label: "LABEL_KEYCODES",
    link: "/keycodes",
    display: function (regionConfig) {
      return regionConfig.features.keycodeAvailable;
    },
  },
  {
    label: "LABEL_MY_ORDERS",
    link: "/myorders",
    display: function (regionConfig, user) {
      return !!user && regionConfig.features.orderHistoryAvailable;
    },
  },
  {
    label: "LABEL_STORE",
    link: "/store",
    display: function (regionConfig) {
      if (regionConfig.store.storeLink) {
        return true;
      }
    },
  },
];

export function AppHeader(props) {
  const [isUserNotLoggedIn, setIsUserNotLoggedIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const {
    user,
    displayNotification,
    setNotificationDisplay,
  } = useUserContext();

  const { resourceLinks } = useLinksContext();
  const navigate = useNavigate();
  const { translate } = useTranslations(["common"]);
  const { isAuthenticating } = useUserContext();
  const { regionData, regionConfig } = useRegionContext();

  const iconsMenu = useMemo(() => {
    const { notificationsAvailable, cartAvailable, faqAvailable } =
      regionConfig.features;

    return [
      notificationsAvailable && {
        label: "LABEL_ALERTS",
        render: () => (
          <NotificationIcon
            showNotification={displayNotification}
            onHide={() => setNotificationDisplay(false)}
          />
        ),
      },
      cartAvailable && {
        label: "LABEL_CART",
        render: (iconProps) => (
          <ShoppingCartIcon
            className={iconProps.className}
            locale={iconProps.locale}
          />
        ),
      },
      faqAvailable && {
        label: "LABEL_FAQ",
        link: "/help",
        icon: "question",
      },
    ].filter(Boolean);
  }, [regionConfig, displayNotification, setNotificationDisplay]);

  const handleNotLoggedInMessage = useCallback(() => {
    setIsUserNotLoggedIn(false);
    setErrorMessage("");
  }, []);

  const handleClick = useCallback((link, newWindow) => {
    const spsLoginAction = () => {
      SpsCallibration.validateSPSLogin()
        .then((html) => {
          let win = window.open("", "");
          setIsUserNotLoggedIn(false);
          setErrorMessage("");
          win.document.write(html);
          win.document.redirectForm.submit();
        })
        .catch(() => {
          setIsUserNotLoggedIn(true);
          setErrorMessage(translate("ERROR_LOGIN_FOR_CALID"));
        });
    };

    const rpoLookupLoginAction = () => {
      RpoLookupService.validateLogin()
        .then(() => {
          setIsUserNotLoggedIn(false);
          setErrorMessage("");
          navigate("/rpolookup");
        })
        .catch(() => {
          setIsUserNotLoggedIn(true);
          setErrorMessage(translate("ERROR_LOGIN_FOR_RPOLOOKUP"));
        });
    };

    const actions = {
      spsInfo: spsLoginAction,
      "/rpolookup": rpoLookupLoginAction,
      default: () => {
        if (newWindow) {
          window.open(link, "_blank");
        } else if (link !== undefined) {
            navigate(link);
        }
      },
    };
    (actions[link] || actions["default"])();
  }, [navigate, translate]);

  const handleLogout = useCallback(() => {
    navigate("/logout");
  }, [navigate]);

  const renderLoadingSpinner = useCallback(() => {
    return <LoadingIcon display={true} size="2x" />
  }, []);

  const renderLogin = useCallback(() => (
    <Login className="login" locale={props.locale} />
  ), [props.locale]);

  const renderLogout = useCallback(() => {
    if (user) {
      const isRegionCN = regionData.regionCode.toUpperCase() === "CN";
      const menuItems = [
        !isRegionCN && (
          <MenuItem
            label={translate("LABEL_PROFILE")}
            title={translate("LABEL_PROFILE")}
            onClick={() => handleClick("/profile")}
          />
        ),
        !isRegionCN && (
          <MenuItem
            label={translate("LABEL_HELP")}
            title={translate("LABEL_HELP")}
            onClick={() => handleClick("/help")}
          />
        ),
        <Divider key="help-logout-divider"/>,
        <MenuItem key="logout" onClick={handleLogout}>
          <i className="fas fa-sign-out-alt pr-1"></i>{" "}
          {translate("LABEL_LOGOUT")}
        </MenuItem>,
      ].filter(Boolean);
      return (
        <div className="logout-div">
          <NavItem>
            <Menu title={user.email} label={user.email} menuAlignment="left">
              {menuItems}
            </Menu>
          </NavItem>
        </div>
      );
    }
    return null;
  }, [user, regionData, translate, handleClick, handleLogout]);

  //If user is authenticating show the spinner. If not authenticating and logged in show logout, else show login
  const renderAuthNavItem = useCallback(() => {
    return isAuthenticating
        ? renderLoadingSpinner()
        : user
            ? renderLogout()
            : renderLogin();

  }, [isAuthenticating, user, renderLoadingSpinner, renderLogout, renderLogin]);

  const renderNavItem = useCallback((navItem) => {
    let dom = (
      <NavItem key={navItem.label}>
        <NavLink to={navItem.link} className={(navData) => navData.isActive ? "active" : "none"} style={{ textDecoration: "none" }}>
          {translate(navItem.label)}
        </NavLink>
      </NavItem>
    );
    if (navItem.hasOwnProperty("display")) {
      return navItem.display(regionConfig, user) ? dom : null;
    } else {
      return dom;
    }
  }, [regionConfig, user, translate]);

  return (
    <div className="appHeader">
      <Navbar
        type="navbar"
        className="tdsNavbar"
        appTitle={
          <ACDelcoLogo
            imageAlt={translate("LABEL_LOGO")}
            onClick={() => handleClick("/")}
          />
        }
      >
        {navItems.map((navItem) => renderNavItem(navItem))}
        <NavItem>
          {resourceLinks && resourceLinks.length > 0 && (
              <Menu label={translate("LABEL_RESOURCES")} className="tdsMenu">
                {resourceLinks.map((menuItem) => (
                    <MenuItem
                        key={menuItem.id}
                        title={menuItem.description}
                        onClick={() => handleClick(menuItem.url, true)}
                    >
                      {menuItem.name}
                    </MenuItem>
                ))}
              </Menu>
          )}
          {isUserNotLoggedIn ? (
            <ResourceLoginCheck
              locale={props.locale}
              isUserNotLoggedIn={isUserNotLoggedIn}
              handleLoginCheck={handleNotLoggedInMessage}
              errorMessage={errorMessage}
            />
          ) : (
            <i></i>
          )}
        </NavItem>
        <NavItemGroup alignment="end">
          <NavItem>
            {renderAuthNavItem()}
          </NavItem>
          {iconsMenu.map((iconMenu) => (
            <NavItem
              key={iconMenu.label}
              title={translate(iconMenu.label)}
              onClick={() => handleClick(iconMenu.link)}
            >
              {iconMenu.render ? (
                iconMenu.render(props)
              ) : (
                <i className={"fas fa-" + iconMenu.icon}></i>
              )}
            </NavItem>
          ))}
          <NavItem className="tdsLanguage">
            <div className="tdsLocaleControls">
              <CountryOverrideSelect onRegionChange={props.onRegionRefresh} />
              <LanguageSelect
                list={regionData.supportedLanguages}
                locale={props.locale}
                onSelect={props.onLocaleChange}
              />
            </div>
          </NavItem>
        </NavItemGroup>
      </Navbar>
    </div>
  );
}

AppHeader.propTypes = {
  locale: PropTypes.string.isRequired,
  onLocaleChange: PropTypes.func,
  onRegionRefresh: PropTypes.func,
};